import { FC } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Typography } from '@altermaind/web/ui';
import { ProgressBar } from '@/_shared/components/ProgressBar';
import { RenderLocalesWithComponents } from '@/_shared/components/RenderLocalesWithComponents';
import { useDialog, useNextRechargeInDays } from '@/_shared/hooks';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getEmployerCreditsCall } from '@/credits/state';
import { HistoryList } from './HistoryList';

export const DialogCreditsUser: FC = () => {
  const t = useTranslations('creditsDomain.dialogCreditsUser');

  const { genericResponsiveValue } = useResponsiveStyle();

  const { closeDialog, Dialog, isOpen } = useDialog('creditsUser');

  const { totalAvailableCredits, totalCredits, consumedCredits } = getEmployerCreditsCall.hook();

  const progress = totalCredits ? (Number(consumedCredits) / Number(totalCredits)) * 100 : 0;

  const { nextMonth, days } = useNextRechargeInDays();

  return (
    <Dialog maxWidth='1000px' open={isOpen} title={undefined} onClose={closeDialog} closeButton>
      <Box width='100%'>
        <Box display='flex' flexDirection='column'>
          <Typography mt='none' variant='h3' color='primary/text' textAlign='center'>
            {t('title')}
          </Typography>
        </Box>

        <Box mb='xs' mt='m' width='100%'>
          <Card backgroundColor='primary'>
            <Box>
              <Box
                mb='xxs'
                display='flex'
                flexDirection={genericResponsiveValue({
                  breakPoints: { desktop: 'row' },
                  defaultValue: 'column',
                })}
                alignItems={genericResponsiveValue({
                  breakPoints: { desktop: 'center' },
                  defaultValue: 'flex-start',
                })}
                justifyContent='space-between'
              >
                <Typography mr='xxxs' variant='p2'>
                  {t('progress.title', { credits: totalAvailableCredits ?? 0 })}
                </Typography>
                <Typography variant='p2/light'>
                  <RenderLocalesWithComponents
                    text={t('progress.nextRecharge', {
                      date: '!{DATE}!',
                      days: days.toString(),
                    })}
                    placeholders={{
                      '!{DATE}!': (
                        <Typography as='span' variant='p2/light' fontWeight='500'>
                          {nextMonth}
                        </Typography>
                      ),
                    }}
                  />
                </Typography>
              </Box>
              <ProgressBar currentPercentage={Number(progress.toFixed(0))} />
              <Box
                alignSelf='flex-end'
                flexDirection='column'
                display='flex'
                justifyContent='flex-end'
                mt='xxs'
                textAlign='right'
              >
                <Typography variant='h4'>{totalCredits}</Typography>
                <Typography variant='p2/light'>{t('progress.totalCredits')}</Typography>
              </Box>
            </Box>
          </Card>
          <HistoryList />
          <Box>
            <Typography mt='m' mb='xxs' variant='h3'>
              {t('howToWork.title')}
            </Typography>
            <Typography variant='p3/light'>{t('howToWork.description')}</Typography>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};
